/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */

"use client";

import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import { Button } from "@heroui/button";
import "swiper/css";

interface GalleryImage {
  image: {
    meta: {
      download_url: string;
    };
    title: string;
  };
}

interface GalleryImageList {
  galleryData: GalleryImage[];
  isSliderAbsolute?: boolean;
  imageLargerSize?: boolean;
}

const AboutUsSlider: React.FC<GalleryImageList> = ({
  galleryData,
  isSliderAbsolute,
  imageLargerSize,
}) => {
  const sliderRef = React.useRef<any>(null);
  const [isReachedEnd, setIsReachedEnd] = useState<boolean>(false);
  const [isReachedStart, setIsReachedStart] = useState(false);

  const handlePrev = () => {
    sliderRef.current.swiper.slidePrev();
  };

  const handleNext = () => {
    sliderRef.current.swiper.slideNext();
  };

  // Extreme End Position Checker
  useEffect(() => {
    if (sliderRef.current?.swiper) {
      const swiper = sliderRef.current.swiper;

      setIsReachedStart(swiper.isBeginning);
      setIsReachedEnd(swiper.isEnd);

      // Listen for changes in swiper state
      swiper.on("slideChange", () => {
        setIsReachedStart(swiper.isBeginning);
        setIsReachedEnd(swiper.isEnd);
      });
    }
  }, [sliderRef]);

  return (
    <div className="flex flex-col w-full relative">
      <Swiper
        ref={sliderRef}
        breakpoints={{
          640: {
            slidesPerView: 2,
            spaceBetween: 24,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 24,
          },
        }}
        className="w-full h-full"
        slidesPerView={1}
        spaceBetween={24}
      >
        {galleryData.map((image, index: number) => (
          <SwiperSlide
            key={index}
            className="!flex w-fit h-full items-center justify-center"
          >
            <Image
              key={index}
              alt={image.image.title}
              className={`aspect-square w-full h-auto max-h-[300px] sm:max-h-[500px] sm:w-auto sm:h-[300px] md:h-[400px] ${imageLargerSize ? "lg:h-[500px]" : ""} object-cover`}
              height={500}
              src={image.image.meta.download_url}
              width={500}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <div
        className={`${isSliderAbsolute ? "absolute bottom-3 z-10" : ""} flex lg:hidden w-full items-center justify-center gap-3 md:gap-4 mt-6`}
      >
        <Button
          isIconOnly
          className="w-10 h-10 bg-[#F6F6F6] rounded-full"
          isDisabled={isReachedStart}
          radius="full"
          variant="flat"
          onPress={handlePrev}
        >
          <img
            alt="Left Arrow"
            className="w-5 h-5 object-contain rotate-180"
            height={20}
            src="/icons/arrow_left_black.svg"
            width={20}
          />
        </Button>
        <Button
          isIconOnly
          className="w-10 h-10 bg-[#F6F6F6] rounded-full"
          isDisabled={isReachedEnd}
          radius="full"
          variant="flat"
          onPress={handleNext}
        >
          <img
            alt="Left Arrow"
            className="w-5 h-5 object-contain"
            height={20}
            src="/icons/arrow_left_black.svg"
            width={20}
          />
        </Button>
      </div>
    </div>
  );
};

export default AboutUsSlider;
